/**
 * PDP buy box — gallery scroll-sync + cross-field variant sync.
 *
 * Ported behavior from pdp_demo_v1.html's assets/global/pdp-immersive.js (thumb rail sticky
 * highlight synced to scroll position, click-to-scroll). The demo's own render()/selectSku()
 * redrew everything from a fake in-memory catalog; here the equivalent variant switch is
 * handled by Horizon's real <variant-picker> (self-morphs its own fieldsets — sequence-grid/
 * color-options — via Shopify's Section Rendering fetch, no code needed here for that part).
 *
 * What *isn't* covered by variant-picker.js's own morph is anything outside the picker's own
 * subtree: title, price, tagline and the gallery. Those listen for the same
 * `shopify:product:select` event variant-picker.js dispatches (StandardEvents.productSelect,
 * see assets/variant-picker.js / assets/product-form.js for the same pattern) and morph
 * themselves from the fetched HTML once its promise resolves.
 *
 * The demo's own scroll-sync code (window.scrollY / window.scrollTo) assumed the window
 * itself scrolls. This theme's layout/theme.liquid wraps everything in a `.page-wrapper` that
 * becomes the *real* scroll container at desktop widths (assets/base.css: `html:has(.page-
 * wrapper)`/`body` get `height:100dvh; overflow:hidden`, `.page-wrapper` gets `overflow-y:
 * auto`) — window.scrollTo is a no-op there, which is exactly why thumb clicks looked broken
 * (sometimes no-op, sometimes a stray partial scroll from the browser's native focus-scroll
 * colliding with the dead window.scrollTo call). Using scrollIntoView()/IntersectionObserver
 * instead sidesteps the question of which element actually scrolls at the current breakpoint.
 */
import { morph } from '@theme/morph';

const buyBox = document.querySelector('.pdp-hero');

if (buyBox) {
  const thumbs = buyBox.querySelector('#pdp-thumbs');
  const main = buyBox.querySelector('#pdp-main-image');
  const picker = buyBox.querySelector('.pdp-variant-picker');

  // Switching Style must implicitly reset Model to that Style's own valid variant (see the
  // file-header comment in sections/pdp-buy-box.liquid). variant-picker.js reads whichever
  // Model radio is *currently* checked when it builds its refetch request, so this has to run
  // in the capture phase to land before variant-picker.js's own bubble-phase `change` listener.
  picker?.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.dataset.fieldsetIndex !== '0') return;

      const defaultModelValueId = input.dataset.defaultModelOptionValueId;
      if (!defaultModelValueId) return;

      const modelInput = picker.querySelector(
        `fieldset[data-fieldset-index="1"] input[data-option-value-id="${defaultModelValueId}"]`
      );
      if (modelInput instanceof HTMLInputElement) modelInput.checked = true;
    },
    true
  );

  let mainFrames = [];
  let frameObserver = null;

  function headerHeight() {
    const header = document.querySelector('.site-header');
    return header ? header.offsetHeight : 0;
  }

  function updateViewportMetrics() {
    const available = Math.max(320, window.innerHeight - headerHeight());
    buyBox.style.setProperty('--pdp-available-h', `${available}px`);
    buyBox.style.setProperty('--pdp-thumb-rail-h', `${available * 0.8}px`);
    buyBox.style.setProperty('--pdp-thumb-rail-offset', `${available * 0.1}px`);
  }

  function setActiveThumb(index) {
    thumbs?.querySelectorAll('.pdp-thumb').forEach((button, i) => {
      const isActive = i === index;
      button.classList.toggle('is-active', isActive);
      if (isActive) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }

  // rootMargin shrinks the observed viewport to a 1px-tall line at vertical center — a frame
  // is "intersecting" exactly when it crosses that line, giving closest-to-center detection
  // without measuring scroll position by hand (see file header comment for why that matters
  // on this theme). Works the same whether `.page-wrapper` or the window is the real scroller.
  function setupFrameObserver() {
    frameObserver?.disconnect();
    if (!mainFrames.length) return;

    frameObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (!visible.length) return;

        const index = mainFrames.indexOf(visible[0].target);
        if (index !== -1) setActiveThumb(index);
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
    );

    mainFrames.forEach((frame) => frameObserver.observe(frame));
  }

  function initGallery() {
    mainFrames = main ? Array.from(main.querySelectorAll('.pdp-main-frame')) : [];

    thumbs?.querySelectorAll('.pdp-thumb').forEach((button) => {
      if (button.dataset.galleryBound === 'true') return;
      button.dataset.galleryBound = 'true';

      button.addEventListener('click', () => {
        const index = Number(button.dataset.mediaIndex);
        const frame = mainFrames[index];
        if (!frame) return;

        setActiveThumb(index);
        frame.scrollIntoView({
          block: 'start',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });
    });

    setupFrameObserver();
  }

  updateViewportMetrics();
  initGallery();

  // iOS Safari auto-hides its address bar / bottom tab bar on scroll-down and restores it on
  // scroll-up. Neither is an actual resize, but each toggle still fires a `resize` event with a
  // different window.innerHeight — recomputing on every one of those made the thumb rail and
  // main image visibly resize/jump mid-scroll (the available-height vars above are keyed off
  // innerHeight). A real resize or orientation change always changes innerWidth too, so gate on
  // that to ignore the browser-chrome-only height churn while still tracking genuine resizes.
  let lastInnerWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastInnerWidth) return;
    lastInnerWidth = window.innerWidth;
    updateViewportMetrics();
  });

  document.addEventListener('shopify:product:select', async (event) => {
    if (!buyBox.contains(event.target)) return;

    let resolved;
    try {
      resolved = await event.promise;
    } catch (error) {
      return;
    }

    const html = resolved?.detail?.html;
    if (!html) return;

    ['#pdp-matrix', '#pdp-title', '#pdp-price', '#pdp-intro'].forEach((selector) => {
      const current = buyBox.querySelector(selector);
      const next = html.querySelector(selector);
      if (current && next) morph(current, next);
    });

    const newThumbs = html.querySelector('#pdp-thumbs');
    const newMain = html.querySelector('#pdp-main-image');
    if (thumbs && newThumbs) morph(thumbs, newThumbs);
    if (main && newMain) morph(main, newMain);

    initGallery();

    if (html.title) document.title = html.title;
  });
}
